import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Rocket, Server, Camera, Bell, ShieldCheck, ArrowRight, Check,
    X as XIcon, Sparkles, BookOpen,
} from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";

/**
 * OnboardingWizard — first-run modal for new tenant admins.
 *
 * Self-fetches its own state on mount: only renders when the backend
 * (`GET /api/me/onboarding`) returns `auto_open: true`. State is
 * persisted per-user via `PATCH /api/me/onboarding`. The modal is
 * dismissable with Skip (sets `dismissed`), Finished (sets
 * `finished`), or the close-X (treats as skip).
 *
 * Five steps, all using endpoints we already shipped — zero new
 * backend surface introduced for the wizard itself.
 */

const STEPS = [
    { key: "welcome",     icon: Rocket,       title: "Welcome to MoovBytes" },
    { key: "install",     icon: Server,       title: "Install your first agent" },
    { key: "migrate",     icon: Camera,       title: "Run your first migration" },
    { key: "notify",      icon: Bell,         title: "Configure notifications" },
    { key: "retention",   icon: ShieldCheck,  title: "Apply SOC2 retention" },
];

export default function OnboardingWizard() {
    const [state, setState] = useState(null);
    const [open, setOpen] = useState(false);
    const [stepIdx, setStepIdx] = useState(0);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await http.get("/me/onboarding");
                if (cancelled) return;
                setState(data);
                if (data?.auto_open) setOpen(true);
            } catch {
                /* silent — wizard is best-effort */
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!open || !state) return null;

    const step = STEPS[stepIdx];
    const isLast = stepIdx === STEPS.length - 1;

    const markStep = async (key) => {
        try {
            await http.patch("/me/onboarding", { completed_step: key });
        } catch { /* non-fatal */ }
    };

    const next = async () => {
        await markStep(step.key);
        if (isLast) return finish();
        setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
    };

    const skip = async () => {
        setBusy(true);
        try {
            await http.patch("/me/onboarding", { dismissed: true });
        } finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const finish = async () => {
        setBusy(true);
        try {
            await http.patch("/me/onboarding", { finished: true });
            toast.success("You're all set. Welcome to MoovBytes!");
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
            setOpen(false);
        }
    };

    const applyDefaults = async () => {
        setBusy(true);
        try {
            await http.post("/settings/retention/apply-defaults");
            toast.success("SOC2 defaults applied across all 5 retention classes.");
            await markStep("retention");
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Welcome wizard"
            data-testid="onboarding-wizard"
            className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        >
            <div className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl">
                {/* Soft brand glow header */}
                <div className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#5b5ef0] via-[#00C2FF] to-[#5b5ef0]" />

                <button
                    type="button"
                    onClick={skip}
                    disabled={busy}
                    aria-label="Close wizard"
                    data-testid="onboarding-close"
                    className="absolute right-3 top-3 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <XIcon size={16} />
                </button>

                <div className="p-6 sm:p-8">
                    <ProgressDots count={STEPS.length} active={stepIdx} />

                    <div className="mt-5 flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#5b5ef0]/15 text-[#5b5ef0]">
                            <step.icon size={18} />
                        </span>
                        <div>
                            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
                                Step {stepIdx + 1} of {STEPS.length}
                            </div>
                            <h2 className="font-display text-xl font-bold leading-tight" data-testid="onboarding-step-title">
                                {step.title}
                            </h2>
                        </div>
                    </div>

                    <div className="mt-5" data-testid={`onboarding-step-${step.key}`}>
                        {step.key === "welcome" && <WelcomeStep />}
                        {step.key === "install" && <InstallStep />}
                        {step.key === "migrate" && <MigrateStep />}
                        {step.key === "notify" && <NotifyStep />}
                        {step.key === "retention" && (
                            <RetentionStep onApply={applyDefaults} busy={busy} />
                        )}
                    </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background/40 px-6 py-3 sm:px-8">
                    <button
                        type="button"
                        onClick={skip}
                        disabled={busy}
                        data-testid="onboarding-skip"
                        className="text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground disabled:opacity-50"
                    >
                        Skip for now
                    </button>
                    <div className="flex items-center gap-2">
                        {stepIdx > 0 && (
                            <button
                                type="button"
                                onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
                                disabled={busy}
                                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest hover:border-foreground/40 disabled:opacity-50"
                            >
                                Back
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={next}
                            disabled={busy}
                            data-testid={isLast ? "onboarding-finish" : "onboarding-next"}
                            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF] px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white shadow-md disabled:opacity-50"
                        >
                            {isLast ? (<><Check size={12} /> Finish</>) : (<>Next <ArrowRight size={12} /></>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function ProgressDots({ count, active }) {
    return (
        <div className="flex items-center gap-1.5" data-testid="onboarding-progress">
            {Array.from({ length: count }).map((_, i) => (
                <div
                    key={i}
                    className={[
                        "h-1 flex-1 rounded-full transition-all",
                        i < active ? "bg-[#5b5ef0]/60" : i === active ? "bg-[#5b5ef0]" : "bg-muted",
                    ].join(" ")}
                />
            ))}
        </div>
    );
}

function WelcomeStep() {
    return (
        <div className="space-y-3 text-sm">
            <p>
                MoovBytes is the migration cockpit for PCs, servers, and NAS storage. In
                the next four steps we&apos;ll show you how to spin up your first agent,
                run a migration, get notified when something breaks, and lock down
                retention so storage doesn&apos;t balloon.
            </p>
            <p className="text-muted-foreground">
                It takes about three minutes. Hit <span className="font-semibold">Next</span> to start.
            </p>
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-border bg-background/40 p-3">
                <BookOpen size={14} className="text-[#00C2FF]" />
                <span className="text-xs text-muted-foreground">
                    Want the full manual? Visit{" "}
                    <Link to="/docs" className="font-semibold text-[#5b5ef0] hover:underline">/docs</Link>{" "}
                    at any time for the user guide.
                </span>
            </div>
        </div>
    );
}

function InstallStep() {
    return (
        <div className="space-y-3 text-sm">
            <p>
                Agents are the workhorse — they live on the source and destination
                machines and stream data between them. Each agent is signed, runs
                inside an elevated session token, and talks to MoovBytes over TLS 1.3.
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {[
                    { os: "Windows", file: ".msi" },
                    { os: "macOS",   file: ".pkg (notarised)" },
                    { os: "Linux",   file: ".deb / .rpm / .tar.gz" },
                ].map((p) => (
                    <div key={p.os} className="rounded-md border border-border bg-background/40 p-2.5 text-center">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{p.os}</div>
                        <div className="mt-0.5 text-xs font-semibold">{p.file}</div>
                    </div>
                ))}
            </div>
            <Link
                to="/access-tokens?tab=deploy-agent"
                data-testid="onboarding-deploy-link"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#5b5ef0]/40 bg-[#5b5ef0]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#5b5ef0] hover:bg-[#5b5ef0]/15"
            >
                <Server size={12} /> Download Agent <ArrowRight size={12} />
            </Link>
        </div>
    );
}

function MigrateStep() {
    return (
        <div className="space-y-3 text-sm">
            <p>
                Once you have one source + one destination agent online, you can fire
                your first migration. Pick a type:
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                <Link to="/migrations" data-testid="onboarding-go-pc" className="group rounded-lg border border-border bg-background/40 p-3 transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md">
                    <div className="font-display font-bold">PC Migration</div>
                    <div className="text-xs text-muted-foreground">Capture apps + registry + settings, then replay.</div>
                </Link>
                <Link to="/migrate/server" data-testid="onboarding-go-server" className="group rounded-lg border border-border bg-background/40 p-3 transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md">
                    <div className="font-display font-bold">Server Migration</div>
                    <div className="text-xs text-muted-foreground">Service-aware data + config move.</div>
                </Link>
                <Link to="/migrate/storage" data-testid="onboarding-go-storage" className="group rounded-lg border border-border bg-background/40 p-3 transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md">
                    <div className="font-display font-bold">Data Migration</div>
                    <div className="text-xs text-muted-foreground">Parallel NAS, Azure, and PC storage engine.</div>
                </Link>
            </div>
        </div>
    );
}

function NotifyStep() {
    return (
        <div className="space-y-3 text-sm">
            <p>
                MoovBytes can ping Slack, Teams, generic webhooks, and email you on
                job completion, failure, or cancellation — with a min-bytes threshold
                so test jobs don&apos;t spam the channel.
            </p>
            <Link
                to="/notifications"
                data-testid="onboarding-notify-link"
                className="inline-flex items-center gap-1.5 rounded-md border border-[#5b5ef0]/40 bg-[#5b5ef0]/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-[#5b5ef0] hover:bg-[#5b5ef0]/15"
            >
                <Bell size={12} /> Open Notifications <ArrowRight size={12} />
            </Link>
        </div>
    );
}

function RetentionStep({ onApply, busy }) {
    return (
        <div className="space-y-3 text-sm">
            <p>
                One-click SOC2-friendly retention: keeps 365 days of audit logs, 90
                days of resolved alerts, 14 days of login attempts, 180 days of
                completed migrations, and the last 30 file versions / 90 days. You
                can tune any class afterwards on the Retention page.
            </p>
            <button
                type="button"
                onClick={onApply}
                disabled={busy}
                data-testid="onboarding-apply-soc2"
                className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#5b5ef0] to-[#00C2FF] px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-white shadow-md disabled:opacity-50"
            >
                <Sparkles size={12} /> Apply SOC2 defaults
            </button>
            <Link to="/retention" className="ml-3 text-xs font-semibold text-muted-foreground hover:text-foreground">
                or fine-tune manually →
            </Link>
        </div>
    );
}
